import config from 'isomorphic-config'

import type { HotelDealMatchReference } from '@/components/TripActivity/types'
import { hotelDealMatchesQuery } from '@/constants/queryFragments'
import analytics from '@/shared-utils/analytics'
import hasGraphErrors from '@/shared-utils/graph-utils'
import isAbortSignalTimeoutSupported from '@/shared-utils/is-abortSignal-supported'
import type { GraphResponse } from '@/types'

import { jsonContentTypeHeader } from '../server/constants'
import getAuthorizationToken from './get-authorization-token'

type RawRecommendation = {
  hotelId: string
  pclnId: string | null
  rank: number
  type: string
}

type RawRecommendationGroup = {
  baseDealId: string
  recommendations: RawRecommendation[]
}

type HotelDealMatchesApiResponse = {
  hotelDealMatches: {
    recommendations: RawRecommendationGroup[]
    references: HotelDealMatchReference[]
  }
}

export type HotelDealMatchRaw = RawRecommendation & {
  reference: HotelDealMatchReference | null
}

type HotelDealMatchesGraphResponse = GraphResponse<HotelDealMatchesApiResponse>

function hasRedeemablePayload(payload: unknown): payload is HotelDealMatchesGraphResponse {
  if (payload && typeof payload === 'object') {
    if ('data' in payload && payload.data && typeof payload.data === 'object') {
      return 'hotelDealMatches' in payload.data
    }
  }
  return false
}

function isValidGraphQLResponse(payload: unknown): payload is HotelDealMatchesGraphResponse {
  if (payload && typeof payload === 'object') {
    if ('errors' in payload && Array.isArray(payload.errors)) {
      return true
    }
    if (
      'data' in payload &&
      payload.data &&
      typeof payload.data === 'object' &&
      'hotelDealMatches' in payload.data
    ) {
      return true
    }
  }
  return false
}

const { url, timeout } = config.client['pcln-graph']

async function fetchHotelDealMatches({
  hotelId,
  checkIn,
  checkOut,
  rooms,
  search,
  appCode,
  appName,
  appVersion,
  cguid,
}: {
  hotelId: string
  checkIn: string
  checkOut: string
  rooms: number
  search: string
  appCode: string
  appName: string
  appVersion: string
  cguid: string
}): Promise<HotelDealMatchRaw[] | null> {
  const authorization = getAuthorizationToken()

  const options = {
    method: 'POST',
    signal: isAbortSignalTimeoutSupported() ? AbortSignal.timeout(timeout) : undefined,
    headers: {
      ...jsonContentTypeHeader,
      'apollographql-client-name': appName,
      'apollographql-client-version': appVersion,
      Authorization: authorization,
    },
    body: JSON.stringify({
      query: hotelDealMatchesQuery,
      variables: {
        appCode,
        checkIn,
        checkOut,
        keys: [{ type: 'hotelId', value: hotelId }],
        responseOptions: {
          retail: { maxResults: 7, cugOnly: false },
        },
        rooms,
        search,
        cguid,
      },
    }),
  } as const

  const requestUrl = `${url}?gqlOp=hotelDealMatches`

  try {
    const response = await fetch(requestUrl, options)
    const { status, url: urlValue } = response
    if (response.ok) {
      const payload = await response.json()
      if (isValidGraphQLResponse(payload)) {
        const payloadHasGraphErrors = hasGraphErrors<HotelDealMatchesApiResponse>(payload)
        const payloadHasRedeemableData = hasRedeemablePayload(payload)
        if (payloadHasGraphErrors) {
          analytics.logError({
            message: 'The hotelDealMatches graph query response has returned errors.',
            errorMessage: payload.errors?.[0]?.message ?? 'Unknown error',
            url: requestUrl,
            appName,
            appVersion,
            errorObjectAsString: JSON.stringify(payload.errors),
            hasRedeemablePayload: payloadHasRedeemableData,
          })
        }
        if (payload.data && (!payloadHasGraphErrors || payloadHasRedeemableData)) {
          const { recommendations: recGroups, references } = payload.data.hotelDealMatches
          const recs = recGroups[0]?.recommendations ?? []
          const refMap = new Map(references.map((r) => [r.hotelId, r]))
          return recs.map((rec) => ({
            ...rec,
            reference: refMap.get(rec.hotelId) ?? null,
          }))
        }
      } else {
        analytics.logError({
          message: 'The hotelDealMatches graph query response is not as per contract',
          payload: JSON.stringify(payload),
        })
      }
    } else {
      analytics.logError({
        message: 'The hotelDealMatches graph query response status is > 299',
        url: urlValue,
        status,
      })
    }
    return null
  } catch (error) {
    const errorMessage =
      error && error instanceof Error ? `${error.message} Error Name: ${error.name}` : 'Unknown error'
    analytics.logError({
      message: 'Failed to call the hotelDealMatches graph query.',
      errorMessage,
      error: JSON.stringify(error),
    })
    return null
  }
}

export default fetchHotelDealMatches
